import * as React from 'react';
import styles from './User.scss';
import className from 'classnames';
import { armoryState, openUrl, settings } from '~/utils/settings';
import { interpolate, t } from '~/utils/localization';
import useClickAway from '~/hooks/useClickAway';
import { isInGameBrowser, arrayToObjectByKey } from '~/utils/utils';
import { logOut } from '~/utils/authMenu';
import RealmTag from '~/components/RealmTag/RealmTag';

interface IUser {
    useProfileIcon?: boolean;
}

const User = (props: IUser) => {
    const ref: React.RefObject<HTMLDivElement> = React.useRef(null);
    const [isVisibleMenu, setVisibleMenu] = React.useState(false);
    const [isVisibleAccountsList, setVisibleAccountsList] = React.useState(false);
    const accountInfo = armoryState.account;
    const realmConfig = arrayToObjectByKey(settings.realmsConfig, 'realm');

    const userNameClassNames = className(styles.userName, {
        [styles.active]: isVisibleMenu,
    });

    const changeMenuVisible = () => {
        if (isVisibleMenu) {
            setVisibleAccountsList(false);
        }
        setVisibleMenu(!isVisibleMenu);
    };

    useClickAway(ref, (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains(styles.userName) || target.classList.contains(styles.userIcon)) {
            return;
        }

        setVisibleMenu(false);
        setVisibleAccountsList(false);
    });

    const goToSupport = () => {
        openUrl(settings.urls.supportUrl);
    };

    const showAccountsList = () => {
        setVisibleAccountsList(true);
    };

    const goToChangeAccount = (realm: string) => {
        if (realm in settings.urls.changeAccountUrls) {
            openUrl(settings.urls.changeAccountUrls[realm], '_self');
        }
    };

    const accountChangeLinks = React.useMemo(() => {
        const links = [];
        if (!accountInfo?.id) {
            return null;
        }
        for (const [key, personaAccount] of Object.entries(accountInfo?.personaInfo)) {
            if (Number(key) !== accountInfo?.id) {
                links.push(
                    <div
                        className={styles.menuItem}
                        onClick={() => {
                            goToChangeAccount(personaAccount.gameRealm);
                        }}
                    >
                        {personaAccount.name}
                        <RealmTag realm={realmConfig[personaAccount.gameRealm]?.shortTitle} />
                    </div>,
                );
            }
        }
        return links;
    }, []);

    if (!accountInfo) {
        const logInUrl = `${settings.urls.loginUrl}?next=${document.location.pathname}`;
        const logInText = interpolate(t('{login_open}Войти{login_close} или {create_open}Создать аккаунт{create_close}'), {
            login_open: `<a href="${logInUrl}">`,
            login_close: `</a>`,
            create_open: `<a href="${settings.urls.registrationUrl}">`,
            create_close: `</a>`,
        });

        return (
            <div className={styles.wrapper}>
                <div className={styles.logInText} dangerouslySetInnerHTML={{ __html: logInText }} />
            </div>
        );
    }

    const personaAccountsSwitchable = Object.keys(accountInfo.personaInfo).length > 1;

    return (
        <React.Fragment>
            <div className={styles.wrapper}>
                {!props.useProfileIcon ? (
                    <div className={userNameClassNames} onClick={changeMenuVisible}>
                        {accountInfo.name}
                        {personaAccountsSwitchable && <RealmTag realm={realmConfig[accountInfo.personaInfo[accountInfo.id]?.gameRealm]?.shortTitle} />}
                    </div>
                ) : (
                    <div className={styles.userIcon} onClick={changeMenuVisible} />
                )}
            </div>
            {isVisibleMenu && (
                <div className={styles.menuWrapper} ref={ref}>
                    {isVisibleAccountsList ? (
                        accountChangeLinks
                    ) : (
                        <>
                            {props.useProfileIcon && <div className={className(styles.menuItem, styles.profileItem)}>{accountInfo.name}</div>}
                            <div className={styles.menuItem} onClick={goToSupport}>
                                {t('Центр поддержки')}
                            </div>
                            {personaAccountsSwitchable && (
                                <div className={className(styles.menuItem, styles.linkItem, styles.changeAccount)} onClick={showAccountsList}>
                                    {t('Change account')}
                                </div>
                            )}
                            {!isInGameBrowser && (
                                <div className={className(styles.menuItem, styles.linkItem, styles.logOut)} onClick={logOut}>
                                    {t('Выйти')}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default User;
